import { Alert } from 'components/Alert';
import { Heading } from 'components/Heading';
import { ContactFormModal } from 'features/ContactFormModal/ContactFormModal';
import { usePrincipals } from 'hooks/queries';
import { getCurrentPrincipal } from 'lib/currentPrincipal';
import { Notification } from 'lib/notifications/notifications';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postPrincipalEdi } from '../../utils/principal-service/principal-endpoints';
import { ContactFormData } from '../../utils/types';

export const EDIInformation: React.FC = () => {
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);

  const { t } = useTranslation();
  const [showContactFormModal, setShowContactFormModal] = useState(false);

  const onModalConfirmHandler = async (contactFormData: ContactFormData) => {
    setShowContactFormModal(false);
    try {
      const selectedInterests = contactFormData.ediProcess.other
        ? [
            ...contactFormData.ediProcess.selected,
            contactFormData.ediProcess.other,
          ]
        : contactFormData.ediProcess.selected;
      await postPrincipalEdi(currentPrincipal.id, {
        additionalInformation: contactFormData.additionalInformation,
        contactPersons: contactFormData.technicalTeams,
        countries: contactFormData.countries,
        selectedInterests,
      });
      Notification({
        message: t('modals:contactFormModal:notification:success'),
      });
    } catch (error) {
      Notification({
        message: t('modals:contactFormModal:notification:failed'),
      });
    }
  };

  return (
    <>
      <div className="max-w-xl-content xl:min-w-content mx-auto">
        <div className="mb-5 mt-6" data-test="page-header">
          <Heading
            text={t('features:collaboration:edi-information:ediPage:heading')}
            margin={0}
            level="h2"
          />
        </div>

        <div className="mb-7 flex flex-row">
          <Alert
            message={t(
              'features:collaboration:edi-information:ediPage:description',
            )}
            flavour="description"
            icon="Support"
            hasBorder={false}
            iconSize="large"
            buttonLabel={t(
              'features:collaboration:edi-information:ediPage:getStarted',
            )}
            onClick={() => setShowContactFormModal(true)}
          />
        </div>

        <div className="mb-7">
          <p className="mb-4">
            {t('features:collaboration:edi-information:ediPage:entrySentence')}
          </p>
          <p className="font-semibold">
            {t(
              'features:collaboration:edi-information:ediPage:whatisEDI:headline',
            )}
          </p>
          <p>
            {t(
              'features:collaboration:edi-information:ediPage:whatisEDI:content',
            )}
          </p>
          <p className="mt-4 font-semibold">
            {t(
              'features:collaboration:edi-information:ediPage:benefitsofEDI:headline',
            )}
          </p>
          <p>
            {t(
              'features:collaboration:edi-information:ediPage:benefitsofEDI:content',
            )}
          </p>
          <ul className="list-disc pl-4 pt-1">
            <li>
              {t(
                'features:collaboration:edi-information:ediPage:benefitsofEDI:bulletPoint1',
              )}
            </li>
            <li>
              {t(
                'features:collaboration:edi-information:ediPage:benefitsofEDI:bulletPoint2',
              )}
            </li>
            <li>
              {t(
                'features:collaboration:edi-information:ediPage:benefitsofEDI:bulletPoint3',
              )}
            </li>
          </ul>
          <p className="mt-4 font-semibold">
            {t(
              'features:collaboration:edi-information:ediPage:currentEDICapabilities:headline',
            )}
          </p>
          <p>
            {t(
              'features:collaboration:edi-information:ediPage:currentEDICapabilities:content',
            )}
          </p>

          <ul className="list-disc pl-4 pt-1">
            <li>
              {t(
                'features:collaboration:edi-information:ediPage:currentEDICapabilities:bulletPoint1',
              )}
            </li>
            <li>
              {t(
                'features:collaboration:edi-information:ediPage:currentEDICapabilities:bulletPoint2',
              )}
            </li>
          </ul>

          <p className="mt-4">
            {t('features:collaboration:edi-information:ediPage:exitSentence')}
          </p>
        </div>
      </div>
      {showContactFormModal && (
        <ContactFormModal
          onCancel={() => setShowContactFormModal(false)}
          onSendContactForm={onModalConfirmHandler}
        />
      )}
    </>
  );
};
